import { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'entities', label: 'Entities' },
  { id: 'clients', label: 'Clients' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'attachments-proposed', label: 'Attachments (proposed)' },
  { id: 'advisors', label: 'Advisors' },
]

// Helper: Get file type icon and display name
const getFileTypeInfo = (mimeType, fileName = '') => {
  if (!mimeType) {
    const ext = fileName.split('.').pop()?.toLowerCase()
    if (ext === 'pdf') return { icon: '📄', type: 'PDF', color: 'bg-red-50 border-red-200 text-red-700' }
    if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return { icon: '🖼️', type: 'Image', color: 'bg-purple-50 border-purple-200 text-purple-700' }
    if (ext === 'docx') return { icon: '📝', type: 'Word Doc', color: 'bg-blue-50 border-blue-200 text-blue-700' }
    return { icon: '📎', type: 'File', color: 'bg-gray-50 border-gray-200 text-gray-700' }
  }

  switch (mimeType) {
    case 'application/pdf':
      return { icon: '📄', type: 'PDF', color: 'bg-red-50 border-red-200 text-red-700' }
    case 'image/jpeg':
    case 'image/png':
    case 'image/webp':
      return { icon: '🖼️', type: 'Image', color: 'bg-purple-50 border-purple-200 text-purple-700' }
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return { icon: '📝', type: 'Word Doc', color: 'bg-blue-50 border-blue-200 text-blue-700' }
    default:
      return { icon: '📎', type: 'File', color: 'bg-gray-50 border-gray-200 text-gray-700' }
  }
}

// Helper: Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function PolicyForm({ initialData, onSave, onClose, sidebarCollapsed, isDarkMode = false }) {
  const { canAccess } = useAuth()
  const [activeTab, setActiveTab] = useState('details')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [providers, setProviders] = useState([])
  const [entities, setEntities] = useState([])
  const [clients, setClients] = useState([])
  const [advisors, setAdvisors] = useState([])
  const [selectedAdvisors, setSelectedAdvisors] = useState([]) // [advisorId, ...]
  const [splits, setSplits] = useState({})                      // { [advisorId]: '60' }
  const [addAdvisorId, setAddAdvisorId] = useState('')
  const [existingAttachments, setExistingAttachments] = useState(initialData?.policy_attachments || [])
  const [newFiles, setNewFiles] = useState([])
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null)
  const [deletingAttachment, setDeletingAttachment] = useState(false)

  // Get the selected attachment object from ID
  const selectedAttachment = existingAttachments.find(a => a.id === selectedAttachmentId)

  const [formData, setFormData] = useState({
    policy_number: initialData?.policy_number || '',
    product_provider_id: initialData?.product_provider_id || '',
    category: initialData?.category || '',
    product: initialData?.product || '',
    entity_id: initialData?.entity_id || '',
    client_id: initialData?.client_id || '',
    start_date: initialData?.start_date || '',
    end_date: initialData?.end_date || '',
    active: initialData?.active ?? true,
  })
  const [entitySuggestion, setEntitySuggestion] = useState(null)

  useEffect(() => {
    Promise.all([
      supabase.from('product_providers').select('id, name').eq('active', true).order('name'),
      supabase.from('entities').select('id, name').eq('active', true).order('name'),
      supabase.from('clients').select('id, full_name, entity_id').eq('active', true).order('full_name'),
      supabase.from('advisors').select('id, full_name').eq('active', true).order('full_name'),
    ]).then(([provData, entData, clientData, advData]) => {
      setProviders(provData.data || [])
      setEntities(entData.data || [])
      setClients(clientData.data || [])
      setAdvisors(advData.data || [])
    })

    if (initialData?.id) {
      supabase
        .from('policy_advisors')
        .select('advisor_id, split_percentage')
        .eq('policy_id', initialData.id)
        .then(({ data }) => {
          setSelectedAdvisors((data || []).map(r => r.advisor_id))
          const s = {}
          ;(data || []).forEach(r => { s[r.advisor_id] = r.split_percentage != null ? String(r.split_percentage) : '' })
          setSplits(s)
        })
    }
  }, [initialData?.id])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (name === 'client_id' && value) {
      const selectedClient = clients.find(c => c.id === value)
      if (selectedClient?.entity_id && !formData.entity_id) {
        const linkedEntity = entities.find(e => e.id === selectedClient.entity_id)
        if (linkedEntity) setEntitySuggestion({ entityId: selectedClient.entity_id, entityName: linkedEntity.name })
      } else {
        setEntitySuggestion(null)
      }
    } else if (name === 'client_id' && !value) {
      setEntitySuggestion(null)
    }
  }

  const addAdvisor = () => {
    if (!addAdvisorId || selectedAdvisors.includes(addAdvisorId)) return
    setSelectedAdvisors(prev => [...prev, addAdvisorId])
    setSplits(prev => ({ ...prev, [addAdvisorId]: '' }))
    setAddAdvisorId('')
  }

  const removeAdvisor = (id) => {
    setSelectedAdvisors(prev => prev.filter(a => a !== id))
    setSplits(prev => { const next = { ...prev }; delete next[id]; return next })
  }

  const updateSplit = (id, val) => {
    setSplits(prev => ({ ...prev, [id]: val }))
  }

  const totalSplit = selectedAdvisors.reduce((sum, id) => sum + (Number(splits[id]) || 0), 0)

  const ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ]
  const MAX_SIZE = 10 * 1024 * 1024 // 10MB

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || [])
    const invalid = files.filter(f => !ALLOWED_TYPES.includes(f.type) || f.size > MAX_SIZE)
    if (invalid.length > 0) {
      const types = invalid.map(f => f.name).join(', ')
      alert(`Invalid file(s): ${types}. Must be PDF, JPG, PNG, WebP, or DOCX under 10MB.`)
      e.target.value = ''
      return
    }
    setNewFiles(prev => [...prev, ...files])
    e.target.value = ''
  }

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index))
  }

  const deleteAttachment = async (attachmentId) => {
    setDeletingAttachment(true)
    try {
      const { error } = await supabase.from('policy_attachments').delete().eq('id', attachmentId)
      if (error) throw new Error(error.message)
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId))
      setSelectedAttachmentId(null)
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    } finally {
      setDeletingAttachment(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.policy_number.trim()) { setError('Policy Number is required'); return }
    setError('')
    setSaving(true)
    try {
      const payload = { ...formData }
      payload.product_provider_id = payload.product_provider_id || null
      payload.entity_id = payload.entity_id || null
      payload.client_id = payload.client_id || null
      // Allow end_date to be optional (policies not yet finalized)
      payload.end_date = payload.end_date || null

      const savedPolicy = await onSave(payload)
      if (!savedPolicy?.id) throw new Error('Failed to save policy')
      const policyId = savedPolicy.id

      // Sync advisors + splits
      await supabase.from('policy_advisors').delete().eq('policy_id', policyId)
      if (selectedAdvisors.length > 0) {
        await supabase.from('policy_advisors').insert(
          selectedAdvisors.map(aid => ({
            policy_id: policyId,
            advisor_id: aid,
            split_percentage: splits[aid] !== '' && splits[aid] != null ? Number(splits[aid]) : null,
          }))
        )
      }

      // Upload new files
      const uploadedAttachments = []
      for (const file of newFiles) {
        const filename = `${policyId}/${Date.now()}_${file.name}`
        const { error: uploadErr } = await supabase.storage.from('policy_attachments').upload(filename, file, { upsert: true })
        if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`)
        const { data: urlData } = supabase.storage.from('policy_attachments').getPublicUrl(filename)
        const { data: insertData, error: dbErr } = await supabase.from('policy_attachments').insert({
          policy_id: policyId,
          file_name: file.name,
          file_url: urlData.publicUrl,
          file_size: file.size,
          mime_type: file.type
        }).select()
        if (dbErr) throw new Error(`Save failed: ${dbErr.message}`)
        if (insertData && insertData.length > 0) {
          uploadedAttachments.push(insertData[0])
        }
      }

      // Add newly uploaded attachments to the list
      if (uploadedAttachments.length > 0) {
        setExistingAttachments(prev => [...prev, ...uploadedAttachments])
        setNewFiles([]) // Clear the pending uploads list
        setSelectedAttachmentId(null) // Deselect current attachment to show updated list
        setError('') // Clear any errors
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const availableAdvisors = advisors.filter(a => !selectedAdvisors.includes(a.id))

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={initialData ? `Edit Policy: ${formData.policy_number}` : 'Add Policy'}
      sidebarCollapsed={sidebarCollapsed}
      isDarkMode={isDarkMode}
      managed
    >
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4 pb-4 min-h-0">
        {error && <p className="text-red-500 text-sm">{error}</p>}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 -mx-6 px-6" style={{ borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors`}
              style={{
                borderBottom: activeTab === tab.id ? '2px solid #F5A623' : '2px solid transparent',
                color: activeTab === tab.id ? '#F5A623' : (isDarkMode ? '#cbd5e1' : '#333333')
              }}
              onMouseEnter={(e) => e.target.style.color = '#F5A623'}
              onMouseLeave={(e) => e.target.style.color = activeTab === tab.id ? '#F5A623' : (isDarkMode ? '#cbd5e1' : '#333333')}
            >
              {tab.label}
              {tab.id === 'advisors' && selectedAdvisors.length > 0 && (
                <span className="ml-1.5 bg-sfp-orange text-white rounded-full px-1.5 py-0.5 text-xs font-bold">
                  {selectedAdvisors.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable tab content */}
        <div className="flex-1 min-h-0 overflow-y-auto">

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Policy Number *</label>
              <input name="policy_number" value={formData.policy_number} onChange={handleChange} className="input-field w-full" placeholder="e.g. 10000147240" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Product Provider</label>
              <select name="product_provider_id" value={formData.product_provider_id} onChange={handleChange} className="input-field w-full">
                <option value="">Select provider...</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Category</label>
              <input name="category" value={formData.category} onChange={handleChange} className="input-field w-full" placeholder="e.g. Banking Employee Benefits" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Product</label>
              <input name="product" value={formData.product} onChange={handleChange} className="input-field w-full" placeholder="e.g. Commercial, Personal" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Start Date</label>
                <input name="start_date" type="date" value={formData.start_date} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>End Date <span className="text-xs text-gray-500">(optional)</span></label>
                <input name="end_date" type="date" value={formData.end_date} onChange={handleChange} className="input-field w-full" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Active</span>
              <button
                type="button"
                onClick={() => setFormData(p => ({ ...p, active: !p.active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.active ? 'bg-sfp-orange' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        )}

        {/* Entities Tab */}
        {activeTab === 'entities' && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Select Entity</label>
              <select name="entity_id" value={formData.entity_id} onChange={handleChange} className="input-field w-full">
                <option value="">Select an entity...</option>
                {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-4 max-w-lg">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Select Client</label>
              <select name="client_id" value={formData.client_id} onChange={handleChange} className="input-field w-full">
                <option value="">Select a client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
            {entitySuggestion && !formData.entity_id && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded p-3">
                <span className="text-amber-500 text-lg flex-shrink-0">🏢</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">This client is linked to <strong>{entitySuggestion.entityName}</strong>.</p>
                  <p className="text-xs text-amber-600 mt-0.5">Apply this entity to the policy?</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button type="button" onClick={() => { setFormData(prev => ({ ...prev, entity_id: entitySuggestion.entityId })); setEntitySuggestion(null) }} className="px-2.5 py-1 bg-amber-600 text-white text-xs font-semibold rounded hover:bg-amber-700 transition">Yes</button>
                  <button type="button" onClick={() => setEntitySuggestion(null)} className="px-2.5 py-1 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded hover:bg-amber-50 transition">No</button>
                </div>
              </div>
            )}
            {formData.entity_id && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                Entity: <strong>{entities.find(e => e.id === formData.entity_id)?.name || formData.entity_id}</strong>
                {' '}— <button type="button" className="underline text-gray-500 hover:text-red-500" onClick={() => setFormData(p => ({ ...p, entity_id: '' }))}>clear</button>
              </p>
            )}
          </div>
        )}

        {/* Attachments Tab */}
        {activeTab === 'attachments' && (
          <div className="space-y-4 max-w-lg">
            {existingAttachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-sfp-darkgray">Saved Attachments ({existingAttachments.length})</p>
                {existingAttachments.map((att) => {
                  const fileInfo = getFileTypeInfo(att.mime_type, att.file_name)
                  return (
                    <div key={att.id} className={`flex items-center justify-between p-3 rounded border ${fileInfo.color}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-lg flex-shrink-0">{fileInfo.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{att.file_name}</p>
                          <p className="text-xs opacity-75">{formatFileSize(att.file_size)}</p>
                        </div>
                      </div>
                      <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="ml-3 flex-shrink-0 text-sfp-orange text-sm font-semibold hover:underline">View File</a>
                    </div>
                  )
                })}
              </div>
            )}
            <div>
              <label htmlFor="policy-attachments-upload" className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Add Attachments</label>
              <input id="policy-attachments-upload" name="policy_attachments_upload" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.docx" onChange={handleFileUpload} className="input-field w-full" />
              <p className="text-xs mt-1" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>PDF, JPG, PNG, WebP, DOCX (max 10MB). Select multiple files at once.</p>
            </div>
            {newFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Pending Upload ({newFiles.length})</p>
                {newFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-green-50 p-3 rounded border border-green-200">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-green-600 flex-shrink-0">📎</span>
                      <span className="text-sm truncate" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>{file.name}</span>
                      <span className="text-xs flex-shrink-0" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>({(file.size / 1024).toFixed(0)} KB)</span>
                    </div>
                    <button type="button" onClick={() => removeNewFile(idx)} className="ml-3 flex-shrink-0 text-red-500 text-sm hover:underline font-medium">Remove</button>
                  </div>
                ))}
              </div>
            )}
            {existingAttachments.length === 0 && newFiles.length === 0 && (
              <p className="text-sm italic" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>No attachments yet. Upload a PDF above.</p>
            )}
          </div>
        )}

        {/* Advisors Tab */}
        {activeTab === 'advisors' && (
          <div className="space-y-4 max-w-lg">
            <p className="text-xs" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Assign advisors and their commission split percentages for this policy.</p>

            {/* Add advisor row */}
            <div className="flex gap-2">
              <select
                id="policy-add-advisor"
                name="add_advisor_id"
                value={addAdvisorId}
                onChange={e => setAddAdvisorId(e.target.value)}
                className="input-field flex-1 text-sm"
              >
                <option value="">+ Select advisor to add...</option>
                {availableAdvisors.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
              </select>
              <Button type="button" variant="secondary" onClick={addAdvisor} disabled={!addAdvisorId}>Add</Button>
            </div>

            {/* Advisor split table */}
            {selectedAdvisors.length > 0 ? (
              <div className="rounded overflow-hidden" style={{ border: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: isDarkMode ? '#1e293b' : '#f9fafb', borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>Advisor</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold w-28" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>Split %</th>
                      <th className="w-16" />
                    </tr>
                  </thead>
                  <tbody style={{ borderColor: isDarkMode ? '#334155' : '#f3f4f6' }} className="divide-y">
                    {selectedAdvisors.map(id => {
                      const advisor = advisors.find(a => a.id === id)
                      return (
                        <tr key={id} style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td className="px-3 py-2 font-medium" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>{advisor?.full_name ?? id}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              {canAccess('commissions_view_all') ? (
                                <>
                                  <input
                                    id={`policy-split-${id}`}
                                    name={`policy_split_${id}`}
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={splits[id] ?? ''}
                                    onChange={e => updateSplit(id, e.target.value)}
                                    className="input-field w-20 py-1 text-sm"
                                    placeholder="0"
                                  />
                                  <span className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>%</span>
                                </>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => removeAdvisor(id)} className="text-red-400 hover:text-red-600 text-xs font-medium">Remove</button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot style={{ backgroundColor: isDarkMode ? '#1e293b' : '#f9fafb', borderTop: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
                    <tr>
                      <td className="px-3 py-2 text-xs font-semibold" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Total</td>
                      <td className="px-3 py-2" colSpan={2}>
                        {canAccess('commissions_view_all') ? (
                          <span className={`text-sm font-bold ${
                            totalSplit === 100 ? 'text-green-600' :
                            totalSplit > 100 ? 'text-red-600' :
                            totalSplit > 0 ? 'text-amber-500' : 'text-gray-400'
                          }`}>
                            {totalSplit.toFixed(totalSplit % 1 !== 0 ? 2 : 0)}%
                            {totalSplit === 100 && ' ✓'}
                            {totalSplit > 100 && ' — exceeds 100%'}
                            {totalSplit > 0 && totalSplit < 100 && ` — ${(100 - totalSplit).toFixed(2)}% unallocated`}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded">
                No advisors assigned. Use the dropdown above to add advisors.
              </p>
            )}
          </div>
        )}

        {/* Attachments (Proposed) Tab */}
        {activeTab === 'attachments-proposed' && (
          <div className="flex gap-4" style={{ height: '600px', maxWidth: 'none' }}>
            {/* Left Panel - Attachments List */}
            <div className={`flex flex-col min-w-0 transition-all ${selectedAttachment ? 'w-64' : 'flex-1'}`}>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3 mb-3" style={{ flexShrink: 0 }}>
                <span className="text-amber-600 flex-shrink-0 text-lg">💡</span>
                <div>
                  <p className="text-sm font-medium text-amber-900">Proposed Enhanced Attachment Viewer</p>
                  <p className="text-xs text-amber-700 mt-0.5">Click any file to preview and manage it. Delete uploaded files on the right panel.</p>
                </div>
              </div>

              {existingAttachments.length === 0 && newFiles.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg flex-1 flex flex-col items-center justify-center" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
                  <span className="text-4xl mb-2">📎</span>
                  <p className="text-sm font-medium" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>No attachments yet</p>
                  <p className="text-xs mt-1" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>Go to the Attachments tab to upload files</p>
                </div>
              ) : (
                <div className="flex flex-col overflow-auto flex-1 space-y-2 pr-2">
                  <h3 className="text-sm font-semibold" style={{ color: isDarkMode ? '#e2e8f0' : '#333333', flexShrink: 0 }}>
                    All Attachments ({existingAttachments.length + newFiles.length})
                  </h3>

                  {/* Saved attachments */}
                  {existingAttachments.map((att) => {
                    const fileInfo = getFileTypeInfo(att.mime_type, att.file_name)
                    const isSelected = selectedAttachment?.id === att.id
                    return (
                      <button
                        key={`att-${att.id}`}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setSelectedAttachmentId(att.id)
                        }}
                        className={`text-left flex items-start gap-3 p-3 rounded-lg border-2 transition-all hover:shadow-md cursor-pointer ${
                          isSelected
                            ? `${fileInfo.color} ring-2 ring-sfp-orange ring-offset-1`
                            : `${fileInfo.color} hover:ring-1 hover:ring-sfp-orange`
                        }`}
                      >
                        <span className="text-xl flex-shrink-0 mt-0.5">{fileInfo.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" title={att.file_name}>{att.file_name}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs opacity-75">
                            <span className="inline-block px-1.5 py-0.5 rounded font-medium">{fileInfo.type}</span>
                            <span>•</span>
                            <span>{formatFileSize(att.file_size)}</span>
                          </div>
                        </div>
                      </button>
                    )
                  })}

                  {/* Pending attachments */}
                  {newFiles.map((file, idx) => {
                    const fileInfo = getFileTypeInfo(file.type, file.name)
                    return (
                      <div key={`new-${idx}`} className={`flex items-start gap-3 p-3 rounded-lg border-2 border-dashed opacity-75 ${fileInfo.color}`}>
                        <span className="text-xl flex-shrink-0 mt-0.5">{fileInfo.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" title={file.name}>{file.name}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs opacity-75">
                            <span className="inline-block px-1.5 py-0.5 rounded font-medium">{fileInfo.type}</span>
                            <span>•</span>
                            <span>{formatFileSize(file.size)}</span>
                            <span>•</span>
                            <span className="italic">Pending</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeNewFile(newFiles.indexOf(file))}
                          className="flex-shrink-0 text-red-600 hover:text-red-700 font-bold text-lg"
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Right Panel - File Details & Preview */}
            {selectedAttachment && (
              <div
                className={`flex-1 flex flex-col rounded-lg border-2 overflow-hidden transition-all ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200 shadow-lg'
                }`}
              >
                {/* Header */}
                <div className={`px-6 py-4 flex items-center justify-between flex-shrink-0 border-b ${isDarkMode ? 'border-slate-700 bg-slate-700' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className="text-lg font-bold" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>File Details</h3>
                  <button
                    type="button"
                    onClick={() => setSelectedAttachmentId(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none hover:bg-gray-200 hover:rounded p-1"
                    title="Close panel"
                  >
                    ✕
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto flex flex-col">
                  {/* File Type Icon & Name */}
                  <div className={`px-6 py-6 border-b ${isDarkMode ? 'border-slate-700 bg-slate-700/50' : 'border-gray-200 bg-gray-50'}`}>
                    {(() => {
                      const fileInfo = getFileTypeInfo(selectedAttachment.mime_type, selectedAttachment.file_name)
                      return (
                        <div className="text-center">
                          <span className="text-7xl block mb-3">{fileInfo.icon}</span>
                          <p className="text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#cbd5e1' : '#999999' }}>{fileInfo.type}</p>
                          <p className="text-base font-bold break-words" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }} title={selectedAttachment.file_name}>
                            {selectedAttachment.file_name}
                          </p>
                        </div>
                      )
                    })()}
                  </div>

                  {/* File Preview */}
                  <div className={`flex-1 p-6 flex items-center justify-center overflow-auto ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                    {(() => {
                      // Determine if it's an image based on mime_type or file extension
                      const isImage = selectedAttachment.mime_type?.startsWith('image/') ||
                        /\.(jpg|jpeg|png|webp|gif|bmp)$/i.test(selectedAttachment.file_name)

                      // Determine if it's a PDF based on mime_type or file extension
                      const isPdf = selectedAttachment.mime_type === 'application/pdf' ||
                        /\.pdf$/i.test(selectedAttachment.file_name)

                      if (isImage) {
                        return (
                          <img
                            src={selectedAttachment.file_url}
                            alt={selectedAttachment.file_name}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.nextElementSibling.style.display = 'block'
                            }}
                          />
                        )
                      } else if (isPdf) {
                        return (
                          <div className="w-full h-full flex flex-col">
                            <iframe
                              src={`${selectedAttachment.file_url}#toolbar=1&navpanes=0`}
                              className="flex-1 rounded-lg border-0 w-full"
                              title={selectedAttachment.file_name}
                            />
                            <p className="text-xs mt-3" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>
                              PDF viewer • Click "Open File" to open in full window
                            </p>
                          </div>
                        )
                      } else {
                        return (
                          <div className="text-center">
                            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Preview not available for this file type</p>
                            <p className="text-xs mt-2" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>Click "Open File" to download or view</p>
                          </div>
                        )
                      }
                    })()}
                  </div>

                  {/* File Details */}
                  <div className={`px-6 py-5 space-y-4 border-t ${isDarkMode ? 'border-slate-700 bg-slate-700/50' : 'border-gray-200 bg-gray-50'}`}>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Size</p>
                      <p className="text-base mt-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>{formatFileSize(selectedAttachment.file_size)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Type</p>
                      <p className="text-base mt-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>{selectedAttachment.mime_type || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Uploaded</p>
                      <p className="text-base mt-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                        {selectedAttachment.created_at ? new Date(selectedAttachment.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={`px-6 py-5 space-y-3 border-t flex-shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <a
                      href={selectedAttachment.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full px-4 py-3 rounded-lg font-semibold text-center text-white bg-sfp-orange hover:bg-orange-700 transition text-base"
                    >
                      Open File
                    </a>
                    <button
                      onClick={() => deleteAttachment(selectedAttachment.id)}
                      disabled={deletingAttachment}
                      className="block w-full px-4 py-3 rounded-lg font-semibold text-center text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-base"
                    >
                      {deletingAttachment ? 'Deleting...' : 'Delete File'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        </div>{/* end scrollable tab content */}

        {/* Form Actions */}
        <div className="flex gap-2 justify-end pt-4 flex-shrink-0" style={{ borderTop: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : initialData ? 'Update Policy' : 'Save Policy'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
